<template>
    <component
        :is="wrapperTag"
        class="s-ribbon s-ribbon-countdown"
        :class="className"
        v-bind="wrapperProps"
    >
        <a-picture
            v-if="normalizedBackgroundImage"
            class="background"
            :background="normalizedBackgroundImage"
            :alt="backgroundImage?.alt || primaryLabel || secondaryLabel"
            fit="cover"
            is-eager
            is-background
        />
        <div class="a-container content">
            <div class="text-wrapper" :class="{ 'promo-ended': eventStarted }">
                <v-clamp
                    v-if="primaryLabel"
                    class="label primary"
                    tag="div"
                    :max-lines="2"
                    :autoresize="true"
                >
                    {{ primaryLabel }}
                </v-clamp>
                <v-clamp
                    v-if="secondaryLabel"
                    class="label secondary"
                    tag="div"
                    :max-lines="2"
                    :autoresize="true"
                >
                    {{ secondaryLabel }}
                </v-clamp>
            </div>
            <div v-if="!eventStarted" class="countdown-wrapper">
                <div class="timer-values">
                    <div
                        v-for="(item, index) in timerData"
                        :key="index"
                        :class="['timer-item', item.class]"
                    >
                        <template v-if="item.separator">
                            <span class="timer-separator">:</span>
                        </template>
                        <template v-else>
                            <div class="timer-digit">
                                {{ item.value }}
                            </div>
                            <div class="timer-unit">
                                {{ item.unit }}
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import VClamp from 'vue3-clamp';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import APicture from '@core/components/picture/picture.vue';
import { useCountdown } from '@core/composables/useCountdown';
import {
    getRibbonStartDate,
    getRibbonTarget,
    normalizeRibbonBackgroundImage,
} from './utils.js';

const props = defineProps({
    labelPrimary: {
        type: String,
        default: '',
    },
    labelAuxiliary: {
        type: String,
        default: null,
    },
    labelCta: {
        type: String,
        default: null,
    },
    linkUrl: {
        type: String,
        default: null,
    },
    styleName: {
        type: String,
        default: 'yellow',
    },
    backgroundImage: {
        type: Object,
        default: undefined,
    },
    backgroundImageMobileId: {
        type: String,
        default: '',
    },
    backgroundImageTabletId: {
        type: String,
        default: '',
    },
    backgroundImageDesktopId: {
        type: String,
        default: '',
    },
    startDate: {
        type: String,
        default: null,
    },
    countdownTargetDate: {
        type: String,
        default: null,
    },
    promoEndedTitle: {
        type: String,
        default: null,
    },
    promoEndedLead: {
        type: String,
        default: null,
    },
    days: {
        type: String,
        default: 'days',
    },
    hours: {
        type: String,
        default: 'hours',
    },
    minutes: {
        type: String,
        default: 'minutes',
    },
    seconds: {
        type: String,
        default: 'seconds',
    },
});

const resolvedStartDate = computed(() => getRibbonStartDate(props) || new Date().toISOString());
const normalizedBackgroundImage = computed(() => normalizeRibbonBackgroundImage(props));
const hasCtaLink = computed(() => Boolean(props.labelCta && props.linkUrl));
const isWrappedWithLink = computed(() => Boolean(props.linkUrl) && !hasCtaLink.value);
const wrapperTag = computed(() => (isWrappedWithLink.value ? DynamicLink : 'div'));
const linkTarget = computed(() => getRibbonTarget(props.linkUrl));

const wrapperProps = computed(() => {
    if (!isWrappedWithLink.value) return {};

    return {
        to: props.linkUrl,
        target: linkTarget.value,
        event: {
            location: 'top ribbon',
            content: props.labelPrimary,
        },
    };
});

const { timerData, eventStarted } = useCountdown({
    get startDate() {
        return resolvedStartDate.value;
    },
    labels: {
        get days() {
            return props.days;
        },
        get hours() {
            return props.hours;
        },
        get minutes() {
            return props.minutes;
        },
        get seconds() {
            return props.seconds;
        },
    },
});

const primaryLabel = computed(() => (
    eventStarted.value
        ? props.promoEndedTitle || props.labelPrimary
        : props.labelPrimary
));
const secondaryLabel = computed(() => (
    eventStarted.value
        ? props.promoEndedLead || props.labelAuxiliary
        : props.labelAuxiliary
));

const className = computed(() => ({
    [props.styleName]: true,
    'is-link': isWrappedWithLink.value,
    'has-background-image': Boolean(normalizedBackgroundImage.value),
}));
</script>

<style lang="pcss" scoped>
.s-ribbon-countdown {
    position: relative;
    z-index: 401;
    display: block;
    width: 100%;
    flex-shrink: 0;
    overflow: hidden;
    transition: background-color linear 0.2s;

    .background {
        position: absolute;
        inset: 0;
        z-index: 0;
    }

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1;
        pointer-events: none;
        background: transparent;
        transition: background-color linear 0.2s;
    }

    .content {
        position: relative;
        z-index: 2;
        display: flex;
        box-sizing: border-box;
        flex-direction: column;
        justify-content: center;
        width: 100%;
        padding: 12px 16px;
        color: var(--av-nav-primary);

        @media (--viewport-mobile-wide) {
            align-items: center;
            padding: 12px 32px;
        }

        @media (--viewport-tablet) {
            flex-direction: row;
        }

        @media (--viewport-desktop) {
            padding: 6px 32px;
        }

        @media (--viewport-desktop-wide) {
            padding: 6px 64px;
        }

        @media (--viewport-desktop-large) {
            padding: 6px 0;
        }

    }

    .text-wrapper {
        display: flex;
        flex-direction: column;
        min-width: 0;
        @media (--viewport-tablet) {
            flex: 1 1 auto;
        }
    }

    .label {
        min-width: 0;
        text-align: center;
        margin-bottom: 16px;
        @media (--viewport-tablet) {
            text-align: start;
            margin: 0;
        }
        &.primary {
            @mixin lead;
            margin-bottom: 8px;
            @media (--viewport-mobile-wide) {
                margin: 0;
            }
            @media (--viewport-desktop) {
                @mixin title;
            }
        }

        &.secondary {
            @mixin descriptor;
            font-weight: 300;

            @media (--viewport-desktop) {
                @mixin paragraph;
                line-height: 20px;
            }
        }
    }

    .countdown-wrapper {
        display: flex;
        justify-content: center;
        width: 100%;
        align-items: center;
        margin: 5px 0;

        @media (--viewport-mobile-wide) {
            margin: 0;
        }

        @media (--viewport-tablet) {
            width: auto;
            margin-inline-start: auto;
        }
    }

    .timer-values {
        display: flex;
        align-items: flex-start;
        text-align: center;
        gap: 0 12px;
        margin-bottom: 8px;
    }

    .timer-digit,
    .timer-separator {
        @mixin title;
        @media (--viewport-desktop) {
            @mixin display;
        }
    }

    .timer-unit {
        font-size: 8px;
        line-height: 10px;
        font-weight: 800;
        text-transform: uppercase;
        @media (--viewport-desktop) {
            font-size: 10px;
            line-height: 12px;
        }
    }

    &.blue,
    &.dark-blue,
    &.navy,
    &.red,
    &.mid-blue,
    &.gradient,
    &.ai-gradient {
        .content,
        .cta {
            color: var(--av-inversed-primary);
            fill: var(--av-inversed-primary);
        }
    }

    &.is-link {
        cursor: pointer;

        &:hover::after {
            background: rgba(17, 24, 39, 0.08);
        }

        &:focus-visible {
            outline: 3px solid var(--av-fixed-focus);
            outline-offset: -3px;
        }
    }

    .cta:hover {
        :deep(.a-link__content) {
            color: var(--av-link-primary-hover);
        }
    }

    .cta:active {
        :deep(.a-link__content) {
            color: var(--av-link-primary-active);
        }
    }

    &.blue,
    &.ai-purple,
    &.dark-blue,
    &.navy,
    &.red,
    &.mid-blue,
    &.gradient,
    &.ai-gradient {
        &.is-link:hover::after {
            background: rgba(255, 255, 255, 0.12);
        }

        .cta:hover {
            :deep(.a-link__content) {
                color: var(--av-link-inversed-hover);
            }
        }

        .cta:active {
            :deep(.a-link__content) {
                color: var(--av-link-inversed-active);
            }
        }

        .cta:focus-visible {
            :deep(.a-link__content) {
                color: var(--av-link-inversed);
            }
        }
    }

    &.yellow {
        background: var(--av-fixed-warning-light);
    }

    &.blue {
        background: var(--av-nav-secondary);
    }

    &.ai-purple {
        background: linear-gradient(75deg, #1F379C 0%, #3849E0 50%, #FC2DF1 100%);
    }

    &.gradient {
        background: linear-gradient(90.05deg, #05245A 3.92%, #2C40C0 44.27%, #EC0004 99.99%);
    }

    &.dark-blue,
    &.navy {
        background: var(--av-nav-primary);
    }

    &.green,
    &.light-green {
        background: var(--av-fixed-success-light);
    }

    &.light-blue {
        background: var(--av-fixed-info-accent);
    }

    &.red {
        background: rgba(193, 43, 34, 1);
    }

    &.mid-blue {
        background: rgba(33, 89, 209, 1);
    }

    &.ai-gradient {
        background: linear-gradient(75deg, #1F379C 0%, #3849E0 50%, #FC2DF1 100%);
    }
}
</style>
